import { Component } from '@theme/component';
import { observeCarouselImages } from '@theme/evenmorn-responsive-images';
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

/** Enhances plain add forms from the shared shell; framework product forms keep their owner. */
class EvenmornCartBridge extends Component {
  #listeners;
  #requests = new Set();
  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    document.addEventListener('submit', this.#submit, { signal: this.#listeners.signal });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    for (const request of this.#requests) request.abort();
    this.#requests.clear();
  }
  #submit = async (event) => {
    const form = event.target;
    // A submit may already be cancelled by a navigation guard. Framework forms
    // have their own AJAX owner; plain cart forms still need this enhancement.
    if (!(form instanceof HTMLFormElement) || form.closest('product-form-component') || event.submitter?.name === 'checkout') return;
    const url = new URL(form.action, location.href);
    const expected = new URL(this.dataset.addUrl, location.origin);
    if (url.origin !== expected.origin || url.pathname.replace(/\.js$/, '') !== expected.pathname.replace(/\.js$/, '')) return;
    const data = new FormData(form);
    // Keep the allocation in drawer AJAX adds; one-time omits the empty native value.
    const sellingPlan = data.get('selling_plan');
    if (sellingPlan) data.set('selling_plan', String(sellingPlan));
    else data.delete('selling_plan');
    if (!data.get('id') || !document.querySelector('#cart-drawer') || !form.checkValidity()) return;
    event.preventDefault();
    if (form.dataset.evCartPending) return;
    form.dataset.evCartPending = 'true';
    const request = new AbortController();
    this.#requests.add(request);
    const deferred = CartLinesUpdateEvent.createPromise();
    const button = event.submitter;
    const disabled = button?.disabled;
    if (button instanceof HTMLButtonElement) button.disabled = true;
    const sections = [...new Set([...document.querySelectorAll('cart-items-component')].map((node) => node.dataset.sectionId).filter(Boolean))];
    data.set('sections', sections.join(','));
    data.set('sections_url', location.pathname);
    form.dispatchEvent(new CartLinesUpdateEvent({ action: 'add', context: 'product', lines: [{ merchandiseId: String(data.get('id')), quantity: Number(data.get('quantity') || 1) }], promise: deferred.promise }));
    let status = form.querySelector('[data-ev-cart-status]');
    if (status) status.textContent = '';
    try {
      const response = await fetch(new URL(this.dataset.addUrl, location.origin), { method: 'POST', body: data, headers: { Accept: 'application/json' }, signal: request.signal });
      const added = await response.json();
      if (!response.ok || added.status || added.errors) throw new Error(typeof added.description === 'string' ? added.description : this.dataset.error);
      const cartResponse = await fetch(new URL(this.dataset.cartUrl, location.origin), { signal: request.signal });
      if (!cartResponse.ok) {
        deferred.resolve({ cart: null, detail: { didError: true } });
        location.assign(this.dataset.cartPage);
        return;
      }
      const cart = await cartResponse.json();
      deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart), detail: { sections: added.sections, items: cart.items, didError: false, source: 'evenmorn-cart-bridge' } });
    } catch (error) {
      deferred.reject(error);
      if (error.name !== 'AbortError' && form.isConnected) {
        if (!status) { status = document.createElement('p'); status.dataset.evCartStatus = ''; status.setAttribute('role', 'alert'); form.append(status); }
        status.textContent = error.message || this.dataset.error;
      }
    } finally {
      delete form.dataset.evCartPending;
      if (button instanceof HTMLButtonElement) button.disabled = Boolean(disabled) || button.dataset.variantAvailable === 'false';
      this.#requests.delete(request);
    }
  };
}

/** Sticky navigation and native menu/search dialogs. */
class EvenmornHeader extends Component {
  /** @type {AbortController | undefined} */
  #listeners;
  #frame = 0;
  #lastY = 0;
  #opener;
  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#lastY = window.scrollY;
    window.addEventListener('scroll', this.#scroll, { passive: true, signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#cartChanged, { signal });
    this.addEventListener('close', this.#restore, { capture: true, signal });
    this.addEventListener('click', this.#backdrop, { signal });
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    cancelAnimationFrame(this.#frame);
    for (const dialog of this.querySelectorAll('dialog[open]')) dialog.close();
  }
  #scroll = () => {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(() => {
      const y = window.scrollY;
      this.classList.toggle('ev-header-hidden', y > 160 && y > this.#lastY && !this.matches(':focus-within') && !this.querySelector('dialog[open]'));
      this.#lastY = y;
      this.#frame = 0;
    });
  };
  /** @param {MouseEvent} event */
  openDialog(event) {
    const control = event.target;
    if (!(control instanceof HTMLElement)) return;
    const dialog = document.getElementById(control.dataset.dialog || '');
    if (!(dialog instanceof HTMLDialogElement)) return;
    event.preventDefault();
    this.#opener = control;
    control.setAttribute('aria-expanded', 'true');
    dialog.showModal();
  }
  /** @param {MouseEvent} event */
  closeDialog(event) { if (event.target instanceof Element) event.target.closest('dialog')?.close(); }
  #restore = () => {
    this.#opener?.setAttribute('aria-expanded', 'false');
    this.#opener?.focus();
  };
  #backdrop = (event) => {
    if (event.target instanceof HTMLDialogElement) {
      const box = event.target.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) event.target.close();
    }
  };
  /** Native link remains usable before enhancement and when the cart drawer is disabled. @param {MouseEvent} event */
  async openCart(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    const drawer = document.getElementById('cart-drawer');
    if (!drawer) return;
    event.preventDefault();
    await customElements.whenDefined('theme-drawer');
    if (this.isConnected && typeof drawer.open === 'function') drawer.open();
  }
  #cartChanged = async (event) => {
    try {
      const result = await event.promise;
      if (!this.isConnected || result?.detail?.didError) return;
      const count = result?.cart?.totalQuantity;
      if (typeof count === 'number') for (const badge of this.querySelectorAll('[data-cart-count]')) badge.textContent = String(count);
    } catch { /* The cart component owns purchase errors. */ }
  };
}

/** Only enhancement hides reveal targets; unenhanced content is always visible. */
class EvenmornReveal extends Component {
  #observer;
  #motion;
  connectedCallback() {
    super.connectedCallback();
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#motion.addEventListener('change', this.#setup);
    this.#setup();
  }
  updatedCallback() { super.updatedCallback(); this.#setup(); }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#motion?.removeEventListener('change', this.#setup);
    for (const node of this.querySelectorAll('.ev-reveal-pending')) node.classList.remove('ev-reveal-pending');
  }
  #setup = () => {
    this.#observer?.disconnect();
    const nodes = this.querySelectorAll('[data-ev-reveal]');
    if (this.#motion?.matches || !('IntersectionObserver' in window)) {
      for (const node of nodes) node.classList.remove('ev-reveal-pending');
      return;
    }
    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) if (entry.isIntersecting) {
        entry.target.classList.remove('ev-reveal-pending');
        this.#observer.unobserve(entry.target);
      }
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    // Batch geometry reads before adding any class that invalidates styles.
    const offscreen = [...nodes].filter(node => node.getBoundingClientRect().top > window.innerHeight);
    for (const node of offscreen) {
      node.classList.add('ev-reveal-pending');
      this.#observer.observe(node);
    }
  };
}

/** Manual carousel: native overflow/swipe, looping arrows and named dots. */
class EvenmornCarousel extends Component {
  /** @type {ResizeObserver | undefined} */
  #observer;
  /** @type {AbortController | undefined} */
  #listeners;
  #frame = 0;
  #index = 0;
  connectedCallback() { super.connectedCallback(); this.#setup(); }
  updatedCallback() { super.updatedCallback(); this.#setup(); }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#listeners?.abort();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }
  #setup() {
    this.#observer?.disconnect();
    this.#listeners?.abort();
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    const track = this.querySelector('[data-carousel-track]');
    if (!(track instanceof HTMLElement)) return;
    this.#listeners = new AbortController();
    observeCarouselImages(track, this.#listeners.signal);
    const update = () => {
      const style = getComputedStyle(track);
      const rtl = style.direction === 'rtl';
      const padding = parseFloat(style.scrollPaddingInlineStart) || 0;
      const edge = track.getBoundingClientRect()[rtl ? 'right' : 'left'] + (rtl ? -padding : padding);
      let distance = Infinity;
      for (const [index, card] of [...track.children].entries()) {
        const delta = Math.abs(card.getBoundingClientRect()[rtl ? 'right' : 'left'] - edge);
        if (delta < distance) { distance = delta; this.#index = index; }
      }
      for (const dot of this.querySelectorAll('[data-slide]')) dot.setAttribute('aria-current', String(Number(dot.dataset.slide) === this.#index));
    };
    const schedule = () => {
      if (this.#frame) return;
      this.#frame = requestAnimationFrame(() => { this.#frame = 0; update(); });
    };
    // Peeked cards can finish scrolling without crossing an intersection threshold.
    track.addEventListener('scroll', schedule, { passive: true, signal: this.#listeners.signal });
    this.#observer = new ResizeObserver(schedule);
    this.#observer.observe(track);
    update();
  }
  /** @param {MouseEvent} event */
  move(event) {
    if (!(event.target instanceof HTMLElement)) return;
    const track = this.querySelector('[data-carousel-track]');
    if (!(track instanceof HTMLElement)) return;
    const current = this.#index;
    const width = track.children[0]?.getBoundingClientRect().width || track.clientWidth;
    const style = getComputedStyle(track);
    const padding = parseFloat(style.scrollPaddingInlineStart) || 0;
    const endPadding = parseFloat(style.scrollPaddingInlineEnd) || 0;
    const visible = Math.max(1, Math.round((track.clientWidth - padding - endPadding) / width));
    const last = track.children.length - visible;
    const index = event.target.hasAttribute('data-slide') ? Math.min(last, Number(event.target.dataset.slide)) : (current + Number(event.target.dataset.direction) + last + 1) % (last + 1);
    const card = track.children[index];
    if (!(card instanceof HTMLElement)) return;
    const rtl = style.direction === 'rtl';
    const edge = rtl ? 'right' : 'left';
    const alignment = track.getBoundingClientRect()[edge] + (rtl ? -padding : padding);
    track.scrollBy({ left: card.getBoundingClientRect()[edge] - alignment, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth' });
  }
}

/** FAQ filter, fragment expansion, and two-second copy-link feedback. */
class EvenmornFAQ extends Component {
  #listeners;
  #timers = new Set();
  #frame = 0;
  connectedCallback() {
    super.connectedCallback();
    this.#listeners = new AbortController();
    window.addEventListener('hashchange', this.#fragment, { signal: this.#listeners.signal });
    this.#fragment();
  }
  updatedCallback() { super.updatedCallback(); this.filter(); this.#fragment(); }
  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    for (const timer of this.#timers) clearTimeout(timer);
    this.#timers.clear();
    cancelAnimationFrame(this.#frame);
  }
  filter() {
    const query = this.querySelector('input[type="search"]')?.value.trim().toLocaleLowerCase() || '';
    let visible = 0;
    for (const group of this.querySelectorAll('[data-faq-group]')) {
      let count = 0;
      for (const question of group.querySelectorAll('details')) {
        question.hidden = !question.textContent.toLocaleLowerCase().includes(query);
        if (!question.hidden) count++;
      }
      group.hidden = count === 0;
      visible += count;
      const anchor = this.querySelector(`[href="#${group.id}"]`);
      if (anchor) anchor.hidden = count === 0;
    }
    const empty = this.querySelector('[data-faq-empty]');
    if (empty) empty.hidden = visible > 0;
    const status = this.querySelector('[data-faq-status]');
    if (status) status.textContent = query ? `${visible} ${this.dataset.resultsLabel}` : '';
  }
  #fragment = () => {
    let id;
    try { id = decodeURIComponent(location.hash.slice(1)); } catch { return; }
    const target = document.getElementById(id);
    if (!(target instanceof HTMLDetailsElement) || !this.contains(target)) return;
    const input = this.querySelector('input[type="search"]');
    if (input?.value) { input.value = ''; this.filter(); }
    target.open = true;
    cancelAnimationFrame(this.#frame);
    this.#frame = requestAnimationFrame(() => { if (this.isConnected) target.scrollIntoView({ block: 'start' }); });
  };
  /** @param {MouseEvent} event */
  async copyLink(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    const question = button.closest('details');
    if (!question) return;
    const url = new URL(location.href);
    url.hash = question.id;
    try {
      await navigator.clipboard.writeText(url.href);
      if (!this.isConnected) return;
      button.textContent = this.dataset.copied;
      const timer = setTimeout(() => { button.textContent = this.dataset.copy; this.#timers.delete(timer); }, 2000);
      this.#timers.add(timer);
    } catch { location.hash = question.id; }
  }
}

for (const [tag, constructor] of Object.entries({
  'evenmorn-header-component': EvenmornHeader,
  'evenmorn-reveal-component': EvenmornReveal,
  'evenmorn-carousel-component': EvenmornCarousel,
  'evenmorn-faq-component': EvenmornFAQ,
  'evenmorn-cart-bridge-component': EvenmornCartBridge,
})) if (!customElements.get(tag)) customElements.define(tag, constructor);
